// src/tattoo/components/TextLayerDialog.jsx
//
// Type tool dialog — shared by Tattune and Merch Ally (exported via
// src/engine). Mounted once per app. It does NOT import any app store;
// the host app passes handlers:
//
//   <TextLayerDialog getLayer={id => layer} onAdd={settings => …} onUpdate={(id, settings) => …} />
//
// Opens via a window event so any button can trigger it without prop-drilling:
//
//   openTextDialog()            -> add a new text layer
//   openTextDialog(layerId)     -> edit an existing text layer
//
// Font tiles are divs (not <button>) on purpose: tattoo-ui.css has a
// global `button { font: ... !important }` rule that would flatten every
// tile to the UI font, defeating the point of previewing typefaces.

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  TEXT_FONTS,
  TEXT_DEFAULTS,
  renderTextLayer,
  preloadAllFonts,
} from "../lib/textLayer";

const OPEN_EVENT = "tt:open-text-dialog";

export function openTextDialog(layerId = null) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { layerId } }));
}

// colour themes: Tattune is dark, MerchMod passes theme="light"
const THEMES = {
  dark: {
    panel: "#161a21", ink: "#e9eef7", line: "#2a2f3b", field: "#0f141a", dim: "#9aa3af",
    swatchLine: "#3a3f4b", on: "rgba(162,51,43,0.18)", scrim: "rgba(0,0,0,0.6)", shadow: "0 24px 80px rgba(0,0,0,0.6)",
  },
  light: {
    panel: "#ffffff", ink: "#1a1a1a", line: "#e4ddd2", field: "#faf7f2", dim: "#7a736b",
    swatchLine: "#d6cfc4", on: "rgba(162,51,43,0.08)", scrim: "rgba(30,24,18,0.35)", shadow: "0 24px 70px rgba(40,30,20,0.25)",
  },
};
const ThemeCtx = createContext(THEMES.dark);

const SWATCHES = ["#111111", "#ffffff", "#a2332b", "#1f3a93", "#0d7845", "#c9a227", "#6b3fa0"];

// `addLabel`: the add button's wording per app ("Add to body" in Tattune,
// "Add to design" in MerchMod).
// small segmented picker used for outline align / corners
function Seg({ value, options, onPick }) {
  const T = useContext(ThemeCtx);
  return (
    <div style={{ display: "flex", gap: 6, flex: 1 }}>
      {options.map(([v, lbl]) => (
        <div
          key={v}
          role="button"
          tabIndex={0}
          aria-pressed={value === v}
          onClick={() => onPick(v)}
          style={{
            cursor: "pointer", flex: 1, textAlign: "center", padding: "6px 0", borderRadius: 8, fontSize: 12,
            border: value === v ? "1px solid #a2332b" : `1px solid ${T.line}`,
            background: value === v ? T.on : T.field,
          }}
        >
          {lbl}
        </div>
      ))}
    </div>
  );
}

// "Team colours" quick row (MerchMod passes the jersey's colours)
function TeamRow({ colours, value, onPick, label }) {
  const T = useContext(ThemeCtx);
  if (!colours?.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
      <span style={{ fontSize: 11, color: T.dim, width: 72 }}>{label}</span>
      {colours.map(([name, c]) => (
        <div
          key={name + c}
          role="button"
          tabIndex={0}
          title={name}
          aria-label={`${label}: ${name}`}
          onClick={() => onPick(c)}
          style={{
            width: 30, height: 30, borderRadius: 8, background: c, cursor: "pointer",
            border: (value || "").toLowerCase() === c.toLowerCase() ? "2px solid #a2332b" : `1px solid ${T.swatchLine}`,
            boxShadow: (value || "").toLowerCase() === c.toLowerCase() ? `0 0 0 2px ${T.panel} inset` : "none",
          }}
        />
      ))}
    </div>
  );
}

export default function TextLayerDialog({ getLayer, onAdd, onUpdate, addLabel = "Add to body", previewBg = null, teamColours = null, theme = "dark", fontOrder = null, defaults = null }) {
  const T = THEMES[theme] || THEMES.dark;
  const fonts = fontOrder
    ? [...fontOrder.map((k) => TEXT_FONTS.find((f) => f.key === k)).filter(Boolean), ...TEXT_FONTS.filter((f) => !fontOrder.includes(f.key))]
    : TEXT_FONTS;
  const [open, setOpen] = useState(false);
  const [layerId, setLayerId] = useState(null);
  const [s, setS] = useState(TEXT_DEFAULTS);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const renderSeq = useRef(0);
  const [fontsOpen, setFontsOpen] = useState(true);
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  // open listener
  useEffect(() => {
    const onOpen = (e) => {
      const id = e.detail?.layerId || null;
      const L = id && getLayer ? getLayer(id) : null;
      setLayerId(L && L.kind === "text" ? id : null);
      setS({ ...TEXT_DEFAULTS, ...(L?.kind === "text" ? L.text : defaultsRef.current || {}) });
      setFontsOpen(!(L && L.kind === "text")); // editing: font already chosen, start collapsed
      setOpen(true);
      preloadAllFonts().then(() => setFontsReady(true));
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [getLayer]);

  // debounced live preview
  useEffect(() => {
    if (!open) return;
    const seq = ++renderSeq.current;
    const t = setTimeout(async () => {
      try {
        const { url } = await renderTextLayer(s);
        if (seq === renderSeq.current) setPreview(url);
      } catch (e) {
        console.warn("[TextLayerDialog] preview failed", e);
      }
    }, 120);
    return () => clearTimeout(t);
  }, [open, s, fontsReady]);

  // esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const patch = (p) => setS((prev) => ({ ...prev, ...p }));

  async function commit() {
    if (!String(s.text || "").trim()) return;
    setBusy(true);
    try {
      if (layerId) await onUpdate?.(layerId, s);
      else await onAdd?.(s);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  const small = typeof window !== "undefined" && window.innerWidth < 600;
  const coarse = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
  const label = { fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: T.dim, margin: small ? "10px 0 5px" : "14px 0 6px" };
  const row = { display: "flex", alignItems: "center", gap: 10 };

  return createPortal(
    <ThemeCtx.Provider value={T}>
    <div
      onClick={() => setOpen(false)}
      style={{ position: "fixed", inset: 0, zIndex: 100000, background: T.scrim, display: "grid", placeItems: "center", padding: small ? 8 : 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={layerId ? "Edit text" : "Add text"}
        style={{
          // flex column: options scroll, the buttons stay pinned in view
          width: "100%", maxWidth: 520, maxHeight: "calc(100dvh - 16px)",
          display: "flex", flexDirection: "column", overflow: "hidden",
          background: T.panel, color: T.ink, border: `1px solid ${T.line}`,
          borderRadius: 16, boxShadow: T.shadow, accentColor: "#a2332b",
        }}
      >
        <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", padding: small ? "14px 14px 6px" : "18px 18px 6px", WebkitOverflowScrolling: "touch" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>
          {layerId ? "Edit text" : "Add text"}
        </div>

        {/* Preview on a skin-ish swatch so light/dark text both read.
            Sticky: it stays in view while you scroll down to the sliders. */}
        <div style={{ position: "sticky", top: 0, zIndex: 2, background: T.panel, boxShadow: `0 -20px 0 ${T.panel}`, margin: small ? "0 -14px" : "0 -18px", padding: small ? "0 14px 4px" : "0 18px 4px" }}>
        <div
          style={{
            position: "relative",
            height: small ? "clamp(96px, 18vh, 150px)" : "clamp(170px, 30vh, 240px)",
            borderRadius: 12, marginBottom: 6, overflow: "hidden",
            background: previewBg || "linear-gradient(135deg, #d9b59a 0%, #c89a7b 100%)",
          }}
        >
          {preview ? (
            // Absolutely positioned + object-fit: contain so the rendered
            // text always scales DOWN to fit the box (a max-height inside a
            // grid cell doesn't constrain, which clipped it on desktop).
            <img
              src={preview}
              alt=""
              style={{
                position: "absolute", inset: 14,
                width: "calc(100% - 28px)", height: "calc(100% - 28px)",
                objectFit: "contain", display: "block",
              }}
            />
          ) : (
            <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "rgba(0,0,0,0.4)", fontSize: 12 }}>
              Rendering…
            </span>
          )}
        </div>
        </div>

        <div style={label}>Text</div>
        <textarea
          value={s.text}
          onChange={(e) => patch({ text: e.target.value })}
          rows={2}
          autoFocus={!coarse}
          placeholder="Type a name, date, quote…"
          style={{
            width: "100%", boxSizing: "border-box", display: "block",
            resize: "vertical", borderRadius: 10, padding: "10px 12px",
            background: T.field, color: T.ink, border: `1px solid ${T.line}`, fontSize: 15,
          }}
        />

        <div
          role="button"
          tabIndex={0}
          aria-expanded={fontsOpen}
          onClick={() => setFontsOpen((v) => !v)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setFontsOpen((v) => !v)}
          style={{ ...label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}
        >
          <span>Font</span>
          {!fontsOpen && (
            <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 13, fontWeight: 600, color: T.ink }}>
              {fonts.find((f) => f.key === s.fontKey)?.label || ""}
            </span>
          )}
          <span style={{ marginLeft: "auto", textTransform: "none", letterSpacing: 0, fontSize: 12, fontWeight: 600, color: "#a2332b" }}>
            {fontsOpen ? "Hide fonts ▴" : "Change font ▾"}
          </span>
        </div>
        {fontsOpen && <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${small ? 88 : 112}px, 1fr))`, gap: small ? 6 : 8 }}>
          {fonts.map((f) => {
            const on = s.fontKey === f.key;
            return (
              <div
                key={f.key}
                role="button"
                tabIndex={0}
                aria-pressed={on}
                onClick={() => patch({ fontKey: f.key })}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && patch({ fontKey: f.key })}
                style={{
                  cursor: "pointer", borderRadius: 10, padding: "8px 6px", textAlign: "center",
                  border: on ? "1px solid #a2332b" : `1px solid ${T.line}`,
                  background: on ? T.on : T.field,
                  userSelect: "none",
                }}
              >
                <div style={{ fontFamily: `"${f.family}", serif`, fontWeight: f.weight, fontSize: small ? 18 : 22, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {(s.text || "Aa").split("\n")[0].slice(0, 10) || "Aa"}
                </div>
                <div style={{ fontSize: 10, color: T.dim, marginTop: 2 }}>{f.label}</div>
              </div>
            );
          })}
        </div>}

        <div style={label}>Colour</div>
        <TeamRow colours={teamColours} value={s.color} onPick={(c) => patch({ color: c })} label="Team colours" />
        <div style={{ ...row, flexWrap: "wrap" }}>
          {SWATCHES.map((c) => (
            <div
              key={c}
              role="button"
              tabIndex={0}
              aria-label={`Colour ${c}`}
              onClick={() => patch({ color: c })}
              style={{
                width: 26, height: 26, borderRadius: "50%", background: c, cursor: "pointer",
                border: s.color.toLowerCase() === c ? "2px solid #a2332b" : `1px solid ${T.swatchLine}`,
                boxShadow: s.color.toLowerCase() === c ? `0 0 0 2px ${T.panel} inset` : "none",
              }}
            />
          ))}
          <input type="color" value={s.color} onChange={(e) => patch({ color: e.target.value })} aria-label="Custom colour" style={{ width: 34, height: 28, padding: 0, border: "none", background: "none" }} />
        </div>

        <div style={label}>Outline</div>
        <TeamRow
          colours={teamColours}
          value={s.outlineWidth > 0 ? s.outlineColor : null}
          onPick={(c) => patch({ outlineColor: c, outlineWidth: s.outlineWidth > 0 ? s.outlineWidth : 3 })}
          label="Team colours"
        />
        <div style={row}>
          <input type="range" min={0} max={24} step={1} value={s.outlineWidth} onChange={(e) => patch({ outlineWidth: Number(e.target.value) })} style={{ flex: 1 }} aria-label="Outline width" />
          <span style={{ width: 28, textAlign: "right", fontSize: 12, color: T.dim }}>{s.outlineWidth}</span>
          <input type="color" value={s.outlineColor} onChange={(e) => patch({ outlineColor: e.target.value })} aria-label="Outline colour" style={{ width: 34, height: 28, padding: 0, border: "none", background: "none" }} />
        </div>
        {s.outlineWidth > 0 && (
          <>
            <div style={{ ...row, marginTop: 8 }}>
              <span style={{ width: 58, fontSize: 12, color: T.dim }}>Align</span>
              <Seg
                value={s.outlineAlign || "outside"}
                options={[["outside", "Outside"], ["center", "Centre"], ["inside", "Inside"]]}
                onPick={(v) => patch({ outlineAlign: v })}
              />
            </div>
            <div style={{ ...row, marginTop: 6 }}>
              <span style={{ width: 58, fontSize: 12, color: T.dim }}>Corners</span>
              <Seg
                value={s.outlineJoin || "round"}
                options={[["round", "Round"], ["miter", "Sharp"], ["bevel", "Bevel"]]}
                onPick={(v) => patch({ outlineJoin: v })}
              />
            </div>
          </>
        )}

        <div style={label}>Curve</div>
        <div style={row}>
          <span style={{ fontSize: 11, color: T.dim, width: 38 }}>Down</span>
          <input
            type="range"
            min={-100}
            max={100}
            step={1}
            value={s.curve ?? 0}
            onChange={(e) => {
              let v = Number(e.target.value);
              if (Math.abs(v) < 4) v = 0; // snap to straight near the middle
              patch({ curve: v });
            }}
            onDoubleClick={() => patch({ curve: 0 })}
            style={{ flex: 1 }}
            aria-label="Curve"
            title="Double-click to straighten"
          />
          <span style={{ fontSize: 11, color: T.dim, width: 22 }}>Up</span>
          <span style={{ width: 34, textAlign: "right", fontSize: 12, color: T.dim }}>
            {(s.curve ?? 0) === 0 ? "—" : s.curve}
          </span>
        </div>

        <div style={label}>Letter spacing</div>
        <div style={row}>
          <input type="range" min={-40} max={60} step={1} value={s.letterSpacing} onChange={(e) => patch({ letterSpacing: Number(e.target.value) })} style={{ flex: 1 }} aria-label="Letter spacing" />
          <span style={{ width: 28, textAlign: "right", fontSize: 12, color: T.dim }}>{s.letterSpacing}</span>
        </div>

        {String(s.text).includes("\n") && (
          <>
            <div style={label}>Alignment</div>
            <div style={row}>
              {["left", "center", "right"].map((a) => (
                <div
                  key={a}
                  role="button"
                  tabIndex={0}
                  aria-pressed={s.align === a}
                  onClick={() => patch({ align: a })}
                  style={{
                    cursor: "pointer", flex: 1, textAlign: "center", padding: "6px 0", borderRadius: 8, fontSize: 12,
                    textTransform: "capitalize",
                    border: s.align === a ? "1px solid #a2332b" : `1px solid ${T.line}`,
                    background: s.align === a ? T.on : T.field,
                  }}
                >
                  {a}
                </div>
              ))}
            </div>
          </>
        )}

        </div>

        {/* pinned footer: always reachable, even on small phones */}
        <div style={{
          ...row, justifyContent: "flex-end", flex: "0 0 auto",
          padding: small ? "10px 14px calc(10px + env(safe-area-inset-bottom))" : "12px 18px 16px",
          borderTop: `1px solid ${T.line}`, background: T.panel,
        }}>
          <button type="button" className="tt-btn tt-btn--danger" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="tt-btn tt-btn--go"
            onClick={commit}
            disabled={busy || !String(s.text || "").trim()}
          >
            {busy ? "Rendering…" : layerId ? "Save text" : addLabel}
          </button>
        </div>
      </div>
    </div>
    </ThemeCtx.Provider>,
    document.body
  );
}
